<template>
	<div class="map-container">
		<!-- Search Bar Inside the Map -->
		<div class="map-search-bar">
			<GmapAutocomplete @place_changed="onPlaceChanged" :placeholder="$t('searchAddress')" />
		</div>

		<!-- Google Map -->
		<div class="map-wrapper">
			<GmapMap :center="mapCenter" :zoom="12" class="map" @click="handleMapClick" @ready="onMapReady">
				<!-- Marker only for search results -->
				<GmapMarker :position="markerPosition" />
				<!-- Circle for radius -->
				<GmapCircle :center="markerPosition" :radius="radius" :options="circleOptions" />
			</GmapMap>

			<!-- Radius Selector Overlay -->
			<div class="radius-selector-overlay">
				<label for="radius">{{ $t('choose_radius') }} (meters):</label>
				<input
					id="radius"
					type="range"
					v-model.number="radius"
					:min="0"
					:max="2000"
					:step="10"
					@input="onRadiusChange"
				/>
				<span>{{ radius }} {{ $t('meters') }}</span>
			</div>
		</div>
	</div>
</template>

<script>
/* eslint-disable no-undef */
// google is a global variable provided by vue2-google-maps
import { Toast } from '@/modules/common/Toast'

export default {
	props: {
		mapCenter: {
			type: Object,
			required: true
		},
		markerPosition: {
			type: Object,
			required: true
		},
		initialRadius: {
			type: Number,
			default: 200 // Default radius in meters
		}
	},
	data() {
		return {
			radius: this.initialRadius, // Local radius value
			circleOptions: {
				strokeColor: '#FF0000',
				strokeOpacity: 0.8,
				strokeWeight: 2,
				fillColor: '#FF0000',
				fillOpacity: 0.35
			},
			geocoder: null
		}
	},
	methods: {
		// Called when GmapMap is ready - Google Maps API is guaranteed to be loaded
		onMapReady() {
			// Initialize geocoder when map is ready
			if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
				this.geocoder = new google.maps.Geocoder();
			} else {
				// Fallback: wait a bit if google.maps isn't immediately available
				this.$nextTick(() => {
					if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
						this.geocoder = new google.maps.Geocoder();
					}
				});
			}
		},
		// Update marker position and emit address based on search
		onPlaceChanged(place) {
			const location = place.geometry.location
			const address = place.formatted_address // Get the formatted address
			const position = {
				lat: location.lat(),
				lng: location.lng()
			}

			this.$emit('update:markerPosition', position)
			this.$emit('addressChanged', address) // Emit the address
		},
		// Emit the updated radius value
		onRadiusChange() {
			this.$emit('radiusChanged', this.radius)
		},
		// Handle map click to drop a pin
		handleMapClick(event) {
			// Get lat and lng from the clicked position
			const position = {
				lat: event.latLng.lat(),
				lng: event.latLng.lng()
			}

			// Update marker position
			this.$emit('update:markerPosition', position)

			// Get address from coordinates using Geocoding
			this.getAddressFromCoordinates(position)
		},
		// Get address from coordinates using Google Geocoding service
		getAddressFromCoordinates(position) {
			// Ensure geocoder is initialized
			if (!this.geocoder) {
				// Try to initialize if Google Maps is available
				if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
					this.geocoder = new google.maps.Geocoder();
					this.performGeocode(position);
				} else {
					// Wait a bit for geocoder to initialize
					setTimeout(() => {
						if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder) {
							this.geocoder = new google.maps.Geocoder();
							this.performGeocode(position);
						} else {
							this.$emit('addressChanged', '');
						}
					}, 300);
				}
			} else {
				this.performGeocode(position);
			}
		},
		// Perform the actual geocoding
		performGeocode(position) {
			if (!this.geocoder) {
				this.$emit('addressChanged', '');
				new Toast({
					message: this.$t('geocoder_not_available') || 'Unable to get address. Google Maps service is not available.',
					type: 'error',
					duration: 4000
				});
				return;
			}
			
			this.geocoder.geocode({ location: position }, (results, status) => {
				if (status === 'OK') {
					if (results[0]) {
						const address = results[0].formatted_address;
						this.$emit('addressChanged', address);
					} else {
						this.$emit('addressChanged', '');
						new Toast({
							message: this.$t('no_address_found') || 'No address found for this location.',
							type: 'warning',
							duration: 3000
						});
					}
				} else {
					this.$emit('addressChanged', '');
					const errorMessages = {
						'ZERO_RESULTS': this.$t('no_address_found') || 'No address found for this location.',
						'OVER_QUERY_LIMIT': this.$t('geocoding_limit_exceeded') || 'Geocoding service limit exceeded. Please try again later.',
						'REQUEST_DENIED': this.$t('geocoding_denied') || 'Geocoding request denied. Please check your Google Maps API key.',
						'INVALID_REQUEST': this.$t('invalid_geocoding_request') || 'Invalid geocoding request.',
					};
					new Toast({
						message: errorMessages[status] || (this.$t('failed_to_get_address') || 'Failed to get address for this location.'),
						type: 'error',
						duration: 4000
					});
				}
			});
		}
	}
}
</script>

<style>
.map-container {
	position: relative;
	width: 100%;
}

.map-wrapper {
	position: relative;
	width: 100%;
	height: 200px;
}

.map-search-bar {
	position: absolute;
	top: 10px;
	right: 100px;
	z-index: 10;
	/* width: calc(100% - 20px); */
	max-width: 400px;
	background: rgba(255, 255, 255, 0.9);
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
	border-radius: 8px;
	padding: 10px;
	
	/* Ensure Google Maps Autocomplete input and dropdown are visible */
	::v-deep input {
		width: 100%;
		padding: 8px;
		border: 1px solid #ccc;
		border-radius: 4px;
	}
}

/* Google Maps Autocomplete dropdown styling */
/* pac-container is rendered at body level by Google Maps */
::v-deep .pac-container {
	z-index: 10000 !important; /* Above modal (9999) when inside modal */
	border-radius: 4px;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
	margin-top: 4px;
	display: block !important; /* Ensure it's visible */
	
	.pac-item {
		padding: 8px 12px;
		cursor: pointer;
		
		&:hover {
			background-color: #f5f5f5;
		}
	}
}

.map {
	height: 100%; /* Keep map filling the wrapper */
	width: 100%;
}

.radius-selector-overlay {
	position: absolute;
	bottom: 10px;
	left: 10px;
	z-index: 20; /* Ensure it's above the map */
	background: rgba(255, 255, 255, 0.9);
	padding: 2px;
	border-radius: 8px;
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 200px;
}

.radius-selector-overlay label {
	font-size: 14px;
	font-weight: bold;
	margin-bottom: 5px;
}

.radius-selector-overlay input[type='range'] {
	width: 100%;
	margin: 5px 0;
}

.radius-selector-overlay span {
	font-size: 12px;
	font-weight: bold;
	color: #333;
}
</style>
